import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ScrollText, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "August 5, 2026";

const Section = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`terms-section-${n}`}>
    <h2 className="font-heading text-xl tracking-tight" style={{ fontWeight: 700 }}>
      {n}. {title}
    </h2>
    <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-foreground/80">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="terms-page">
        <Link to="/" data-testid="terms-back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ScrollText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Terms of Service</h1>
            <p className="mt-1 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <p className="mt-6 text-[15px] leading-relaxed text-foreground/80">
          Welcome to BogoShare. These Terms of Service (the "Terms") are a legal agreement between you and BogoShare
          ("BogoShare", "we", "us", or "our") and govern your access to and use of our website, applications, and
          services (together, the "Platform"). Please read them carefully. By creating an account, clicking "I agree",
          or otherwise using the Platform, you confirm that you accept these Terms. If you do not agree, do not use the
          Platform.
        </p>

        <Section n={1} title="What BogoShare Does">
          <p>
            BogoShare is an online marketplace that helps two people share the value of "buy one, get one" (BOGO) and
            similar multi-item retail promotions. One member (the "Poster") posts interest in a deal; another member
            (the "Partner") agrees to pay an agreed fee to take a portion of that deal. The Partner's fee is collected
            by BogoShare and held pending confirmation, and the Poster receives a payout after the deal is completed
            and confirmed.
          </p>
          <p>
            BogoShare is a facilitator and payment intermediary only. We do not sell the underlying retail goods, are
            not a party to the in-store or in-app purchase between you and any retailer, and are not affiliated with,
            endorsed by, or sponsored by any retailer named on the Platform. All retailer names and trademarks belong
            to their respective owners and are used for factual identification only.
          </p>
        </Section>

        <Section n={2} title="Eligibility & Accounts">
          <p>
            You must be at least the age of majority in your province or territory of residence and capable of forming
            a binding contract to use the Platform. The Platform is intended for users located in Canada.
          </p>
          <p>
            To use most features you must create an account and verify your email address using the one-time code we
            send you. You are responsible for the accuracy of the information you provide, for keeping your credentials
            confidential, and for all activity that occurs under your account. Notify us immediately of any unauthorized
            use. We may offer optional two-step verification and trusted-device management to help protect your account.
          </p>
        </Section>

        <Section n={3} title="Deals, Codes & Member Conduct">
          <p>
            Deal listings may be curated by BogoShare or submitted by members. We do not guarantee that any deal is
            available, accurately described, in stock, honoured by a retailer, or free of error. You are responsible for
            reviewing a retailer's own terms and confirming a deal's availability before relying on it.
          </p>
          <p>You agree that you will not:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>post false, misleading, expired, or fraudulent deals or codes;</li>
            <li>share a redemption code you do not have the right to share, or use a code in violation of a retailer's terms;</li>
            <li>attempt to obtain a payout without genuinely completing your side of a deal, or otherwise defraud another member or BogoShare;</li>
            <li>circumvent the Platform to arrange payment off-Platform in order to avoid fees;</li>
            <li>harass, abuse, or impersonate others, or collect other members' personal information; or</li>
            <li>interfere with, scrape, or attempt to gain unauthorized access to the Platform.</li>
          </ul>
          <p>
            You are solely responsible for your dealings with other members. BogoShare does not control and is not
            responsible for the conduct of any member.
          </p>
        </Section>

        <Section n={4} title="Payments, Escrow & Fees">
          <p>
            When a Partner agrees to a deal, the agreed fee is authorized and/or collected through our third-party
            payment processor and held by BogoShare pending completion of the deal. Card processing costs are absorbed
            by BogoShare unless stated otherwise at the time of a transaction. BogoShare retains an administrative fee
            as disclosed during the transaction; the remainder is credited toward the Poster's payout.
          </p>
          <p>
            Funds are released to the Poster only after the applicable confirmation and holding conditions are met.
            Amounts credited to your in-app wallet may be withdrawn to you by Interac e-Transfer or another payout
            method we support. Payouts may be processed manually by our team or through an automated payout provider,
            and may be subject to review, verification, and reasonable holding periods to prevent fraud.
          </p>
          <p>
            You authorize BogoShare and its payment partners to charge, hold, capture, release, and refund amounts as
            described above and as required to operate the Platform. You are responsible for providing accurate payout
            details; BogoShare is not responsible for funds sent to an incorrect address that you provided.
          </p>
        </Section>

        <Section n={5} title="Refunds, Cancellations & Disputes">
          <p>
            Because deals depend on the actions of members and retailers, all sales and fees are generally final once a
            deal is confirmed. Where a deal cannot be completed through no fault of the Partner, we will use reasonable
            efforts to reverse the corresponding pre-authorization or issue a refund of the fee, less any non-recoverable
            costs. If you have a dispute with another member, contact us at admin@bogoshare.com and we will review the
            matter. Our determination of a dispute is made in good faith but is not a substitute for legal advice or a
            formal adjudication.
          </p>
        </Section>

        <Section n={6} title="Taxes">
          <p>
            You are responsible for determining and paying any taxes that apply to your use of the Platform, including
            on any amounts you receive as a Poster. BogoShare may collect applicable taxes on its fees where required by
            law.
          </p>
        </Section>

        <Section n={7} title="Communications">
          <p>
            By creating an account you agree to receive service-related communications from us (for example, verification
            codes, security alerts, payout notices, and dispute correspondence) by email. These are necessary to operate
            your account and are not "marketing" messages. You may manage optional communications where such controls are
            provided.
          </p>
        </Section>

        <Section n={8} title="Intellectual Property">
          <p>
            The Platform, including its design, text, graphics, and software, is owned by BogoShare or its licensors and
            is protected by intellectual-property laws. We grant you a limited, revocable, non-exclusive, non-transferable
            licence to use the Platform for its intended purpose. You may not copy, modify, distribute, or create
            derivative works from the Platform except as permitted by these Terms.
          </p>
        </Section>

        <Section n={9} title="Privacy">
          <p>
            Your use of the Platform is also governed by our{" "}
            <Link to="/privacy" className="font-semibold text-primary hover:underline" data-testid="terms-privacy-link">Privacy Policy</Link>,
            which explains how we collect, use, and protect your information. By using the Platform you consent to those
            practices.
          </p>
        </Section>

        <Section n={10} title="Disclaimers">
          <p>
            The Platform is provided on an "as is" and "as available" basis. To the fullest extent permitted by law,
            BogoShare disclaims all warranties, express or implied, including merchantability, fitness for a particular
            purpose, and non-infringement. We do not warrant that the Platform will be uninterrupted, error-free, or
            secure, or that any deal, code, or payout will meet your expectations.
          </p>
        </Section>

        <Section n={11} title="Limitation of Liability">
          <p>
            To the fullest extent permitted by law, BogoShare and its directors, officers, employees, and agents will not
            be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of
            profits, data, or goodwill, arising out of or relating to your use of the Platform. Our total aggregate
            liability for any claim relating to the Platform will not exceed the greater of (a) the total fees BogoShare
            earned from your transactions in the three (3) months before the event giving rise to the claim, or (b)
            CAD $100.
          </p>
        </Section>

        <Section n={12} title="Indemnification">
          <p>
            You agree to indemnify and hold harmless BogoShare from any claims, liabilities, damages, and expenses
            (including reasonable legal fees) arising out of your use of the Platform, your deals or codes, your breach
            of these Terms, or your violation of any law or the rights of a third party (including any retailer).
          </p>
        </Section>

        <Section n={13} title="Suspension & Termination">
          <p>
            We may suspend or terminate your access to the Platform at any time, with or without notice, if we believe
            you have violated these Terms, engaged in fraud, or created risk or legal exposure for BogoShare or others.
            You may stop using the Platform at any time. Provisions that by their nature should survive termination
            (including payment obligations, disclaimers, limitations of liability, and indemnities) will survive.
          </p>
        </Section>

        <Section n={14} title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. When we do, we will revise the "Last updated" date above and,
            where appropriate, notify you. Your continued use of the Platform after changes take effect constitutes
            acceptance of the revised Terms.
          </p>
        </Section>

        <Section n={15} title="Governing Law">
          <p>
            These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable
            therein, without regard to conflict-of-laws rules. You agree to the exclusive jurisdiction of the courts
            located in Ontario, Canada for any dispute not otherwise resolved, subject to any mandatory consumer
            protections of your home province or territory.
          </p>
        </Section>

        <Section n={16} title="Contact">
          <p>
            Questions about these Terms? Reach us at{" "}
            <a href="mailto:admin@bogoshare.com" className="font-semibold text-primary hover:underline" data-testid="terms-contact-email">admin@bogoshare.com</a>.
          </p>
        </Section>

        <div className="mt-10 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="terms-legal-notice">
          <b>Please note:</b> This document is a general template provided for convenience and does not constitute legal
          advice. BogoShare recommends having these Terms reviewed by a qualified lawyer to ensure they suit your
          business and comply with the laws of every jurisdiction in which you operate.
        </div>
      </main>
    </div>
  );
}
